import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import Flex from "../shared/Flex";
import Editor, { useMonaco } from "@monaco-editor/react";
import { editor } from "monaco-editor";

export interface JsonEditorProps {
    defaultValue: string;
    validationSchema: ValidationSchema;
    hasErrorsChanged: (hasErrors: boolean) => void;
    textChanged: (newText: string) => void;
}

export enum ValidationSchema {
    SpaceConfig = "space.json",
    WebsiteConfig = "website.json",
    FeatureFlagConfig = "featureflag.json",
    Unknown = "file",
}

export default function JsonEditor(props: JsonEditorProps): JSX.Element {
    const [errors, setErrors] = useState([]);
    const [hasTodo, setHasTodo] = useState(false);
    const editorRef = useRef(null);
    const monaco = useMonaco();

    const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor, monaco) => {
        editorRef.current = editor;
        onChange(editor.getValue(), null); // refresh initial error state for to do-s
        setErrors([]);
    };

    useEffect(() => {
        if (!monaco) {
            return;
        }
        monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
            schemas: [
                {
                    uri: "https://space",
                    fileMatch: ["space.json"],
                    schema: {
                        "name": "New Space Schema",
                        "type": "object",
                        "properties": {
                            "slug": {
                                "title": "Slug",
                                "type": "string",
                                "description": "A portion of the URL that is used to identify the space uniquely.",
                                "minLength": 2,
                                "maxLength": 64
                            },
                            "displayName": {
                                "title": "Display Name",
                                "type": "string",
                                "description": "Space name displayed to the customer.",
                                "minLength": 2,
                                "maxLength": 64
                            },
                            "info": {
                                "title": "Space description/info",
                                "type": ["string", "null"],
                                "description": "null if no info, otherwise space info that shows up in Report Problem/Suggest Feature views.",
                                "maxLength": 280
                            },
                            "ado": {
                                "title": "Azure Devops Instance to use",
                                "type": "object",
                                "properties": {
                                    "instance": {
                                        "type": "string",
                                        "enum": [
                                            "devdiv.visualstudio.com",
                                            "devdiv-test.visualstudio.com",
                                            "WorkItem.Sources.TeamFoundation.VSOnline",
                                            "onees.visualstudio.com",
                                            "office.visualstudio.com",
                                            "devdiv_online_services",
                                            "devdiv_test_devdiv",
                                            "msazure_antares",
                                            "msazure_one",
                                            "msazure_liftr",
                                            "msazure_oneagile",
                                            "msdata_sqltoolsandlibraries",
                                            "domoreexp_msteams",
                                        ],
                                        "description": "Server instance",
                                    },
                                    "defaultAreaPath": {
                                        "type": "string",
                                        "description": "Area path to assign new or moved feedback to.",
                                        "minLength": 2,
                                        "maxLength": 256
                                    }
                                },
                                "required": [
                                    "instance",
                                    "defaultAreaPath"
                                ]
                            },
                            "showNavTo": {
                                "type": ["array", "null"],
                                "items": {
                                    "type": "string",
                                    "enum": [
                                        "MicrosoftEmployees",
                                        "Admins",
                                        "Moderators",
                                        "Users",
                                        "Ring0",
                                        "MVP",
                                    ]
                                },
                                "description": "null to show space in navigation to everyone, otherwise group names",
                            },
                            "showContentTo": {
                                "type": ["array", "null"],
                                "items": {
                                    "type": "string",
                                    "enum": [
                                        "MicrosoftEmployees",
                                        "Admins",
                                        "Moderators",
                                        "Users",
                                        "Ring0",
                                        "MVP",
                                    ]
                                },
                                "description": "null to show contents/posts to everyone, otherwise group names",
                            },
                        },
                        "required": [
                            "slug",
                            "displayName",
                            "ado",
                            "showNavTo",
                            "showContentTo",
                        ]
                    }
                },
                {
                    uri: "https://website",
                    fileMatch: ["website.json"],
                    schema: {
                        "name": "New Website Schema",
                        "type": "object",
                        "properties": {
                            "key": {
                                "title": "Website group key",
                                "type": "string",
                                "description": "Unique key identifying the website group",
                                "minLength": 2,
                                "maxLength": 64
                            },
                            "hostNames": {
                                "title": "Host Names",
                                "type": "array",
                                "description": "Host names that belong to this group, usually includes the production URL as well as any staging or regional hosts.",
                                "items": {
                                    "type": "string",
                                    "minLength": 2,
                                    "maxLength": 128
                                },
                                "minItems": 1
                            },
                            "showSpaces": {
                                "title": "Show Spaces",
                                "type": "array",
                                "description": "Ordered list of space slugs for spaces visible on this site",
                                "items": {
                                    "type": ["string", "object"],
                                    "properties": {
                                        "key": {
                                            "type": "string"
                                        },
                                        "displayName": {
                                            "type": "string"
                                        },
                                        "spaces": {
                                            "type": "array",
                                            "items": {
                                                "type": "string",
                                                "minLength": 2,
                                                "maxLength": 128
                                            }
                                        }
                                    },
                                    "required": [
                                        "key",
                                        "displayName",
                                        "spaces"
                                    ],
                                    "minLength": 2,
                                    "maxLength": 128
                                }
                            },
                            "title": {
                                "title": "Website title",
                                "type": "string",
                                "description": "Website title",
                                "minLength": 2,
                                "maxLength": 128
                            },
                        },
                        "required": [
                            "key",
                            "hostNames",
                            "showSpaces",
                            "title",
                        ]
                    }
                },
                {
                    uri: "https://featureflag",
                    fileMatch: ["featureflag.json"],
                    schema: {
                        "name": "Feature Flag Schema",
                        "type": "object",
                        "properties": {
                            "featureName": {
                                "title": "Feature Name",
                                "type": "string",
                                "description": "Name of feature flag",
                                "minLength": 1,
                                "maxLength": 280
                            },
                            "featureValue": {
                                "title": "Feature Value",
                                "type": "string",
                                "description": "Value for feature flag",
                                "minLength": 1,
                                "maxLength": 500000
                            }
                        },
                        "required": [
                            "featureName",
                            "featureValue",
                        ]
                    }
                }
            ],
            allowComments: true,
        });
    }, [monaco]);

    const onValidate = (markers: editor.IMarker[]) => {
        const newErrors = [];
        markers.forEach(marker => {
            const message: string = `${marker.startLineNumber}:${marker.startColumn} ${marker.message}`;
            newErrors.push(message);
        });

        if (newErrors.length > 0 || newErrors.length !== errors.length) {
            setErrors(newErrors);
        }
    }

    const onChange = (value: string | undefined, ev: editor.IModelContentChangedEvent | null) => {
        const newHasTodo = (value ?? "").indexOf("TODO") >= 0;
        if (newHasTodo !== hasTodo) {
            setHasTodo(newHasTodo);
        }
        props.textChanged(value ?? "");
    }

    useEffect(() => {
        props.hasErrorsChanged(hasTodo || errors.length > 0);
    }, [hasTodo, errors]);

    return (
        <Container>
            <Flex densityHor="normal" className="vertical-space" css="width: 100%">
                <div css="flex: 1 1 60%">
                    Edit configuration below. Use Ctrl+M to tab out.
                    <Editor options={{ minimap: { enabled: false }, overviewRulerLanes: 1 }}
                        theme="vs-dark"
                        height="40vh"
                        defaultLanguage="json"
                        defaultValue={props.defaultValue}
                        className="editor"
                        path={props.validationSchema}
                        onValidate={onValidate}
                        onChange={onChange}
                        onMount={handleEditorDidMount}
                    />
                </div>
            </Flex>
            <div className="validation">
                <p>Problems ({errors.length + (hasTodo ? 1 : 0)})</p>
                {hasTodo && <div className="red-text">
                    Fix all TODOs.
                </div>}
                {errors.length > 0 && <div className="red-text">
                    {errors.map(error => <p key={error}>{error}</p>)}
                </div>}
            </div>

        </Container>
    );
}

const Container = styled.div`
    position: relative;

    .red-text {
        color: red;
    }

    .vertical-space {
        margin-bottom: 2rem;
    }
    
    .editor, .editor-ro {
        height: 100%;
        width: 100%;
        min-height: 100%;
        min-width: 100%;
    }
`;

